/**
 * AppInsightsMetricsClient (PROD-592 + PROD-593).
 *
 * Live Mode implementation of the `MetricsClient` interface. PROD-593
 * lands the `@azure/monitor-query` and `@azure/identity` deps into
 * `package.json`; the SDK is reached via the PROD-589 dynamic-import
 * shim so tests stay clean (no real network calls per HC9) and the
 * bundle does not pay the SDK cost when Live Mode is never reached.
 *
 * Architecture:
 *   - The import specifier is resolved through an injected
 *     `DynamicImporter`. Production calls `import('@azure/monitor-query')`
 *     and `import('@azure/identity')` via
 *     `import(/* @vite-ignore *\/ mod)` so Vite leaves the optional
 *     deps alone at build time.
 *   - Authentication uses `DefaultAzureCredential` from
 *     `@azure/identity` (the AKS pod inherits the
 *     `KEY_VAULT_CLIENT_ID` / `KEY_VAULT_CLIENT_KEY` SP credentials
 *     and AAD-backed identity, matching the Scrips microservices
 *     pattern).
 *   - The `workspaceId` option carries the Log Analytics workspace
 *     id from runtime config (`appInsightsWorkspaceId`, populated by
 *     `docker-entrypoint.sh` from K8s env var `APPINSIGHTS_WORKSPACE_ID`
 *     sourced from `devkeyvault`). Required for `queryWorkspace`; when
 *     null, the client returns `null` from `snapshot` so the caller
 *     falls back to `DemoMetricsClient`.
 *   - When the SDK is not bundled or auth / workspace id fails, every
 *     method resolves to a "no data" sentinel: zero counts, empty
 *     maps, and `snapshot` returns `null`.
 *   - When the SDK is bundled, the client queries App Insights with
 *     KQL and projects the rows into the typed interface shape.
 *
 * HC4 ML/RAG: the `confidenceTimelinePerNba` method maps to a KQL
 * query that groups `customEvents` named `nba_confidence_adjusted`
 * by `customDimensions.nba_id` and orders by `timestamp`.
 *
 * HC5: zero em / en dashes anywhere.
 * HC9: tests inject the dynamic importer with a stubbed SDK; no real
 * Azure calls fire under vitest.
 */
import type { MetricsClient } from './MetricsClient';
import {
  DEFAULT_TIME_WINDOW,
  LIVE_LEARNING_EVENT_NAMES,
  type ConfidencePoint,
  type LiveLearningEventName,
  type MetricsSnapshot,
  type OverrideRate,
  type PersonalizationPoint,
  type TimeWindow,
} from './types';
import type { TenantId } from '../../state/types';
import type { NbaState } from '../../types/nba';

/**
 * Configuration for the live client. The connection string is the
 * App Insights resource ID; the workspace id is the Log Analytics
 * workspace GUID that backs that resource. Both are sourced from
 * runtime config in production.
 */
export interface AppInsightsMetricsClientOptions {
  /** App Insights connection string (required when the SDK is bundled). */
  connectionString: string;
  /**
   * Log Analytics workspace id used by `LogsQueryClient.queryWorkspace`.
   * When null or absent, every method short-circuits to "no data" and
   * `snapshot` returns null so the caller falls back to Demo. PROD-593
   * INFRA GATE wires this from K8s env `APPINSIGHTS_WORKSPACE_ID`.
   */
  workspaceId?: string | null;
  /**
   * Optional dependency injection of the dynamic importer so tests
   * can drive both the "SDK present" and "SDK absent" branches
   * without spinning up the real package.
   */
  dynamicImport?: DynamicImporter;
  /**
   * Optional clock injection for `snapshot.fetchedAt`. Production
   * uses `() => new Date().toISOString()`.
   */
  now?: () => string;
}

/**
 * Loosely-typed handle on the App Insights / Log Analytics query
 * client. Production binds against `LogsQueryClient` from
 * `@azure/monitor-query`; this minimal shape is what the wiring
 * layer calls today and what the future implementation will adapt
 * to.
 */
export interface AppInsightsQueryClient {
  /**
   * Execute a KQL query against the configured workspace and
   * return a row array. Implementations MAY shape rows however
   * the underlying SDK exposes them; the wiring layer adapts to
   * the typed projections used here.
   */
  queryLogs: (kql: string) => Promise<ReadonlyArray<Record<string, unknown>>>;
}

export type DynamicImporter = (mod: string) => Promise<unknown>;

export const defaultDynamicImport: DynamicImporter = (mod) =>
  import(/* @vite-ignore */ mod);

/**
 * KQL query templates. Documented here so the wiring layer can hook
 * them without re-reading the spec. Each template uses Application
 * Insights' `customEvents` table; the timestamp range is bound at
 * query time.
 */
export const KQL_QUERIES = {
  eventCountsByType: `
    customEvents
    | where name in ('override_event', 'mode_switch', 'tenant_change',
      'nba_confidence_adjusted', 'lab_basket_confirmation_required')
    | where timestamp between (datetime({startIso}) .. datetime({endIso}))
    | summarize count() by name
  `,
  nbaStateRatio: `
    customEvents
    | where name == 'nba_confidence_adjusted'
    | summarize count() by tostring(customDimensions.next_state)
  `,
  beforeAfterByNba: `
    customEvents
    | where name == 'nba_confidence_adjusted'
    | summarize
        before = avg(todouble(customDimensions.previous_confidence)),
        after = avg(todouble(customDimensions.next_confidence))
      by tostring(customDimensions.nba_id)
  `,
  overrideRateByTenant: `
    customEvents
    | where name == 'override_event'
    | summarize count() by tostring(customDimensions.tenant_id)
  `,
  overrideRateTrend: `
    customEvents
    | where name == 'override_event'
    | summarize count() by bin(timestamp, 1d), tostring(customDimensions.tenant_id)
    | order by timestamp asc
  `,
  confidenceTimelinePerNba: `
    customEvents
    | where name == 'nba_confidence_adjusted'
    | project timestamp, nba_id = tostring(customDimensions.nba_id),
      next_confidence = todouble(customDimensions.next_confidence)
    | order by timestamp asc
  `,
} as const;

/**
 * Try to resolve the App Insights query client by lazy-loading the
 * SDK. Returns `null` when the SDK is not bundled, the credential
 * cannot be acquired, or the constructor throws. Mirrors the PROD-589
 * LLM router pattern.
 *
 * Real-SDK shape: `LogsQueryClient` from `@azure/monitor-query` is
 * constructed with a `TokenCredential` (we use
 * `DefaultAzureCredential` from `@azure/identity`). Queries go through
 * `queryWorkspace(workspaceId, kql, timespan)` which returns
 * `{ tables: [{ rows, columnDescriptors }] }`. The adapter projects
 * the row arrays into the `Record<string, unknown>[]` shape the
 * wiring layer expects, and also keeps a `queryLogs(kql)` shortcut so
 * the existing test stubs (which expose `queryLogs` directly) keep
 * working without churn.
 */
export async function tryResolveQueryClient(
  config: AppInsightsMetricsClientOptions,
  importer: DynamicImporter = defaultDynamicImport,
): Promise<AppInsightsQueryClient | null> {
  let mod: unknown;
  try {
    mod = await importer('@azure/monitor-query');
  } catch {
    return null;
  }
  // The SDK exposes `LogsQueryClient` as a constructor. Accept either
  // a default export or a named export so the wiring is robust to
  // future SDK reshuffles.
  type LogsQueryClientCtor = new (
    credentialOrConnectionString: unknown,
  ) => unknown;
  const Ctor =
    (mod as { LogsQueryClient?: LogsQueryClientCtor }).LogsQueryClient ??
    (mod as { default?: LogsQueryClientCtor }).default;
  if (typeof Ctor !== 'function') return null;

  // Resolve a TokenCredential lazily. Production uses
  // DefaultAzureCredential (resolves the SP credentials the AKS pod
  // already inherits via KEY_VAULT_*); when the identity SDK is not
  // bundled we fall back to passing the connection string directly so
  // existing test stubs (which accept a string) continue to work.
  let credential: unknown = config.connectionString;
  try {
    const identityMod = await importer('@azure/identity');
    type DefaultAzureCredentialCtor = new () => unknown;
    const DefaultCred =
      (identityMod as { DefaultAzureCredential?: DefaultAzureCredentialCtor })
        .DefaultAzureCredential ??
      (identityMod as { default?: DefaultAzureCredentialCtor }).default;
    if (typeof DefaultCred === 'function') {
      try {
        credential = new DefaultCred();
      } catch {
        // Credential init failed; fall back to the connection-string path.
      }
    }
  } catch {
    // @azure/identity not bundled (test path). Stick with the connection
    // string fallback so the tests' constructor stubs remain compatible.
  }

  let raw: unknown;
  try {
    raw = new Ctor(credential);
  } catch {
    return null;
  }
  type QueryWorkspaceResult = {
    tables?: ReadonlyArray<{
      columnDescriptors?: ReadonlyArray<{ name?: string }>;
      columns?: ReadonlyArray<{ name?: string }>;
      rows?: ReadonlyArray<ReadonlyArray<unknown>>;
    }>;
  };
  const adapter: AppInsightsQueryClient = {
    queryLogs: async (kql: string) => {
      const handle = raw as {
        queryLogs?: (q: string) => Promise<ReadonlyArray<Record<string, unknown>>>;
        queryWorkspace?: (
          workspaceId: string,
          q: string,
          timespan: unknown,
        ) => Promise<QueryWorkspaceResult>;
      };
      if (typeof handle.queryLogs === 'function') {
        // Test-stub fast path: stubs expose `queryLogs(kql)` directly.
        return handle.queryLogs(kql);
      }
      if (typeof handle.queryWorkspace === 'function') {
        const wsId = config.workspaceId ?? null;
        if (!wsId) {
          console.warn(
            '[metrics/app-insights] workspaceId missing; falling back to no-data',
          );
          return [];
        }
        const result = await handle.queryWorkspace(wsId, kql, {
          duration: 'P7D',
        });
        return projectQueryWorkspaceResult(result);
      }
      // SDK shape mismatch: surface as no-data so the dashboard
      // falls back to Demo. Log so the operator can investigate.
      console.warn(
        '[metrics/app-insights] SDK exposes neither queryLogs nor queryWorkspace; returning empty',
      );
      return [];
    },
  };
  return adapter;
}

/**
 * Project a `LogsQueryClient.queryWorkspace` result into the
 * `Record<string, unknown>[]` shape the wiring layer expects. The
 * real SDK returns rows as positional arrays paired with a column
 * descriptor list; we zip them into key-value records keyed by
 * column name.
 */
function projectQueryWorkspaceResult(
  result:
    | { tables?: ReadonlyArray<{
        columnDescriptors?: ReadonlyArray<{ name?: string }>;
        columns?: ReadonlyArray<{ name?: string }>;
        rows?: ReadonlyArray<ReadonlyArray<unknown>>;
      }> }
    | undefined,
): ReadonlyArray<Record<string, unknown>> {
  const table = result?.tables?.[0];
  if (!table) return [];
  const cols = table.columnDescriptors ?? table.columns ?? [];
  const rows = table.rows ?? [];
  const colNames = cols.map((c, i) =>
    typeof c?.name === 'string' && c.name.length > 0 ? c.name : `col${i}`,
  );
  return rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (let i = 0; i < colNames.length; i += 1) {
      out[colNames[i]] = row[i];
    }
    return out;
  });
}

/**
 * Build an empty snapshot. Used both by the SDK-absent branch and as
 * the type-safe default when a query returns zero rows.
 */
function emptySnapshot(window: TimeWindow, fetchedAt: string): MetricsSnapshot {
  const counts: Record<LiveLearningEventName, number> = {
    override_event: 0,
    mode_switch: 0,
    tenant_change: 0,
    nba_confidence_adjusted: 0,
    lab_basket_confirmation_required: 0,
  };
  const ratio: Record<NbaState, number> = {
    active: 0,
    reduced: 0,
    restored: 0,
  };
  const rateByTenant: Record<TenantId, OverrideRate> = {
    'apex-cardiometabolic': 0,
    'longevity-health-institute': 0,
  };
  const trend: Record<TenantId, PersonalizationPoint[]> = {
    'apex-cardiometabolic': [],
    'longevity-health-institute': [],
  };
  // Plan v9 §4.2 prototype-anchored fields. Live Mode without a Real
  // signal source returns empty / zero defaults; the view owns the
  // empty-state copy. Demo Mode supplies the canned baseline.
  return {
    window,
    fetchedAt,
    eventCounts: counts,
    nbaStateRatio: ratio,
    beforeAfterByNba: {},
    overrideRateByTenant: rateByTenant,
    overrideRateTrend: trend,
    confidenceTimelinePerNba: {},
    liveSignals: [],
    eventsIndexedCount: 0,
    suggestionQualityRows: [],
    clinicIntelligenceByTenant: {
      'apex-cardiometabolic': {
        providerName: '',
        clinicLabel: '',
        personalizationScore: 0,
        personalizationDelta: 0,
        precisionMetrics: [],
      },
      'longevity-health-institute': {
        providerName: '',
        clinicLabel: '',
        personalizationScore: 0,
        personalizationDelta: 0,
        precisionMetrics: [],
      },
    },
    learningBulletsByTenant: {
      'apex-cardiometabolic': [],
      'longevity-health-institute': [],
    },
    azureStackRows: [],
    hasLiveData: false,
  };
}

/**
 * Construct an App Insights-backed metrics client. The client is
 * cheap to build; the SDK resolution happens lazily on first
 * `snapshot` call so the bundle does not pay the cost when Live Mode
 * is never reached.
 */
export function createAppInsightsMetricsClient(
  options: AppInsightsMetricsClientOptions,
): MetricsClient {
  const importer = options.dynamicImport ?? defaultDynamicImport;
  const now = options.now ?? (() => new Date().toISOString());
  let cachedClient: AppInsightsQueryClient | null | undefined = undefined;

  async function resolve(): Promise<AppInsightsQueryClient | null> {
    if (cachedClient !== undefined) return cachedClient;
    cachedClient = await tryResolveQueryClient(options, importer);
    if (cachedClient === null) {
      console.warn(
        '[metrics/app-insights] SDK not bundled; metrics fall back to Demo Mode (PROD-593 INFRA GATE)',
      );
    }
    return cachedClient;
  }

  /**
   * Helper: project a `count() by name` row set into the typed
   * event-count map. Unknown names are silently ignored so a stray
   * event in the workspace cannot wedge the dashboard.
   */
  function projectEventCounts(
    rows: ReadonlyArray<Record<string, unknown>>,
  ): Record<LiveLearningEventName, number> {
    const out: Record<LiveLearningEventName, number> = {
      override_event: 0,
      mode_switch: 0,
      tenant_change: 0,
      nba_confidence_adjusted: 0,
      lab_basket_confirmation_required: 0,
    };
    for (const row of rows) {
      const name = String(row.name ?? '');
      if ((LIVE_LEARNING_EVENT_NAMES as readonly string[]).includes(name)) {
        const count = Number(row.count_ ?? row.Count ?? 0);
        out[name as LiveLearningEventName] = Number.isFinite(count) ? count : 0;
      }
    }
    return out;
  }

  const eventCountsByType: MetricsClient['eventCountsByType'] = async (
    window,
  ) => {
    const client = await resolve();
    if (client === null) {
      return {
        override_event: 0,
        mode_switch: 0,
        tenant_change: 0,
        nba_confidence_adjusted: 0,
        lab_basket_confirmation_required: 0,
      };
    }
    const endIso = window.endIso ?? now();
    const startIso = new Date(
      new Date(endIso).getTime() - window.days * 24 * 60 * 60 * 1000,
    ).toISOString();
    const kql = KQL_QUERIES.eventCountsByType
      .replace('{startIso}', startIso)
      .replace('{endIso}', endIso);
    const rows = await client.queryLogs(kql);
    return projectEventCounts(rows);
  };

  const nbaStateRatio: MetricsClient['nbaStateRatio'] = async () => {
    const client = await resolve();
    if (client === null) {
      return { active: 0, reduced: 0, restored: 0 };
    }
    const rows = await client.queryLogs(KQL_QUERIES.nbaStateRatio);
    let total = 0;
    const counts: Record<NbaState, number> = {
      active: 0,
      reduced: 0,
      restored: 0,
    };
    for (const r of rows) {
      const k = String(r.next_state ?? '');
      const c = Number(r.count_ ?? r.Count ?? 0);
      if (k === 'active' || k === 'reduced' || k === 'restored') {
        counts[k] += Number.isFinite(c) ? c : 0;
        total += Number.isFinite(c) ? c : 0;
      }
    }
    if (total === 0) return counts;
    return {
      active: Math.round((counts.active / total) * 10_000) / 10_000,
      reduced: Math.round((counts.reduced / total) * 10_000) / 10_000,
      restored: Math.round((counts.restored / total) * 10_000) / 10_000,
    };
  };

  const beforeAfterByNba: MetricsClient['beforeAfterByNba'] = async () => {
    const client = await resolve();
    if (client === null) return {};
    const rows = await client.queryLogs(KQL_QUERIES.beforeAfterByNba);
    const out: Record<string, { before: number; after: number }> = {};
    for (const r of rows) {
      const id = String(r.nba_id ?? '');
      if (!id) continue;
      const before = Number(r.before ?? 0);
      const after = Number(r.after ?? 0);
      out[id] = {
        before: Number.isFinite(before) ? before : 0,
        after: Number.isFinite(after) ? after : 0,
      };
    }
    return out;
  };

  const overrideRateByTenant: MetricsClient['overrideRateByTenant'] = async () => {
    const client = await resolve();
    if (client === null) {
      return {
        'apex-cardiometabolic': 0,
        'longevity-health-institute': 0,
      };
    }
    const rows = await client.queryLogs(KQL_QUERIES.overrideRateByTenant);
    const out: Record<TenantId, OverrideRate> = {
      'apex-cardiometabolic': 0,
      'longevity-health-institute': 0,
    };
    let total = 0;
    const counts: Record<TenantId, number> = {
      'apex-cardiometabolic': 0,
      'longevity-health-institute': 0,
    };
    for (const r of rows) {
      const tenant = String(r.tenant_id ?? '');
      const c = Number(r.count_ ?? r.Count ?? 0);
      if (tenant === 'apex-cardiometabolic' || tenant === 'longevity-health-institute') {
        counts[tenant] += Number.isFinite(c) ? c : 0;
        total += Number.isFinite(c) ? c : 0;
      }
    }
    if (total === 0) return out;
    out['apex-cardiometabolic'] =
      Math.round((counts['apex-cardiometabolic'] / total) * 10_000) / 10_000;
    out['longevity-health-institute'] =
      Math.round((counts['longevity-health-institute'] / total) * 10_000) /
      10_000;
    return out;
  };

  const overrideRateTrend: MetricsClient['overrideRateTrend'] = async () => {
    const client = await resolve();
    const empty: Record<TenantId, PersonalizationPoint[]> = {
      'apex-cardiometabolic': [],
      'longevity-health-institute': [],
    };
    if (client === null) return empty;
    const rows = await client.queryLogs(KQL_QUERIES.overrideRateTrend);
    const out: Record<TenantId, PersonalizationPoint[]> = {
      'apex-cardiometabolic': [],
      'longevity-health-institute': [],
    };
    for (const r of rows) {
      const tenant = String(r.tenant_id ?? '');
      if (tenant !== 'apex-cardiometabolic' && tenant !== 'longevity-health-institute') {
        continue;
      }
      const ts = String(r.timestamp ?? '');
      const rate = Number(r.rate ?? r.count_ ?? 0);
      out[tenant as TenantId].push({
        ts,
        rate: Number.isFinite(rate) ? rate : 0,
      });
    }
    return out;
  };

  const confidenceTimelinePerNba: MetricsClient['confidenceTimelinePerNba'] =
    async () => {
      const client = await resolve();
      if (client === null) return {};
      const rows = await client.queryLogs(KQL_QUERIES.confidenceTimelinePerNba);
      const out: Record<string, ConfidencePoint[]> = {};
      for (const r of rows) {
        const id = String(r.nba_id ?? '');
        if (!id) continue;
        const ts = String(r.timestamp ?? '');
        const conf = Number(r.next_confidence ?? 0);
        const pt: ConfidencePoint = {
          ts,
          confidence: Number.isFinite(conf) ? conf : 0,
        };
        const series = out[id] ?? [];
        series.push(pt);
        out[id] = series;
      }
      return out;
    };

  const snapshot: MetricsClient['snapshot'] = async (window) => {
    const w = window ?? DEFAULT_TIME_WINDOW;
    const client = await resolve();
    if (client === null) {
      // SDK not bundled / auth init failed. Return null so the caller
      // falls back to the DemoMetricsClient. The caller is the
      // learning-loop store; it owns the fallback decision.
      return null;
    }
    const fetchedAt = now();
    const [counts, ratio, beforeAfter, rateByTenant, trend, timeline] =
      await Promise.all([
        eventCountsByType(w),
        nbaStateRatio(),
        beforeAfterByNba(),
        overrideRateByTenant(),
        overrideRateTrend(),
        confidenceTimelinePerNba(),
      ]);
    // Match the empty-shape default but flip the live-data flag so
    // the dashboard banner updates.
    const base = emptySnapshot(w, fetchedAt);
    return {
      ...base,
      eventCounts: counts,
      nbaStateRatio: ratio,
      beforeAfterByNba: beforeAfter,
      overrideRateByTenant: rateByTenant,
      overrideRateTrend: trend,
      confidenceTimelinePerNba: timeline,
      hasLiveData: true,
    };
  };

  return {
    eventCountsByType,
    nbaStateRatio,
    beforeAfterByNba,
    overrideRateByTenant,
    overrideRateTrend,
    confidenceTimelinePerNba,
    snapshot,
  };
}
